import React, { useContext, useEffect, useState } from "react";
import styles from "./StockTransferTable.module.css";
import { IBranch } from "types/Branch";
import { DataTableWithDates } from "components/dataTableWithDates/DataTableWithDates";
import { IStockTransfer, IStockTransfersByBranch } from "types/StockTransfer";
import {
  useQueryStockTransfers,
  invalidateStockTransferCache,
} from "hooks/StockTransfers";
import {
  approveStockTransfer,
  commentOnStockTransfer,
  updateStockTransferEntry,
} from "api/PostStockTransfer";
import { deleteStockTransfer } from "api/DeleteStockTransfer";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import { ApprovalDialogWithComment } from "components/approvalDialogWithComment/ApprovalDialogWithComment";
import { ConfirmationDialog } from "components/confirmationDialog/ConfirmationDialog";
import { StatusWithComments } from "./StatusWithComments";
import { FMSError } from "types/Error";
import { useQuerySelectedFromToDates } from "hooks/SelectedFromToDates";
import {
  displayNameForUnitOfMeasure,
  IDisplayQuantity,
  MeasurementUnit,
} from "types/MeasurementUnit";
import { Status } from "types/Status";
import { getPendingStockTransfers } from "api/GetStockTransfers";
import moment from "moment";
import CSVDownload from "components/utils/CSVDownload";
import { getStockTransferDataForExport } from "lib/CSVExportUtils";
import { Auth } from "lib/Auth";
import { Avatar, CircularProgress, Input } from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "assets/DeleteIcon";
import { useMutation } from "react-query";
import { FMSThemeContext } from "contexts/FMSThemeContext";

const isUserAuthorizedToModify = () => {
  return Auth.getInstance().isAdminOrSupervisor();
};

interface IPostStockTransferEntry {
  id: string;
  quantity: number;
  unit: MeasurementUnit;
}

const EditableQuantity = (props: {
  stockTransfer: IStockTransfer;
  showToast: (props: IToastBasicProps) => void;
  quantityUpdated: (newQuantity: IDisplayQuantity) => void;
}) => {
  const { showToast, stockTransfer, quantityUpdated } = props;
  const initialQuantity = stockTransfer.displayQuantity;
  const [showEditView, setShowEditView] = useState(false);
  const [quantityObj, setQuantityObj] = useState(initialQuantity);
  const [isEditing, setIsEditing] = useState(false);

  const [mutateForUpdateStockTransferEntry] = useMutation(
    (post: IPostStockTransferEntry) => {
      const value: number = post.quantity;
      const shouldConvertToGmsOrMl =
        post.unit === MeasurementUnit.KILOGRAMS ||
        post.unit === MeasurementUnit.LITRE;
      const convertedQuantity = shouldConvertToGmsOrMl ? value * 1000 : value;
      setIsEditing(true);
      return updateStockTransferEntry(post.id, convertedQuantity);
    },
    {
      onSuccess: (error) => {
        setIsEditing(false);
        if (!error) {
          showToast({
            open: true,
            message: "Stock transfer quantity updated successfully",
            type: "success",
          });
          initialQuantity.quantity = quantityObj.quantity;
          quantityUpdated(quantityObj);
          setShowEditView(false);
        } else {
          setQuantityObj(initialQuantity);
          const message = error.message ?? "Something went wrong!";
          showToast({
            open: true,
            message: message,
            type: "error",
          });
        }
      },
    }
  );

  if (!isUserAuthorizedToModify()) {
    return (
      <>{`${initialQuantity.quantity} ${displayNameForUnitOfMeasure(
        initialQuantity.unit
      )}`}</>
    );
  }

  return (
    <>
      {showEditView && (
        <>
          <Input
            type="number"
            value={quantityObj.quantity}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              const quantity = isNaN(value) ? 0 : value;
              setQuantityObj({
                unit: quantityObj.unit,
                quantity: quantity,
              });
            }}
          />
          <button
            onClick={async () => {
              if (quantityObj.quantity > 0) {
                if (quantityObj.quantity === initialQuantity.quantity) {
                  setShowEditView(false);
                } else {
                  mutateForUpdateStockTransferEntry({
                    ...quantityObj,
                    id: stockTransfer.id,
                  });
                }
              } else {
                setQuantityObj(initialQuantity);
                showToast({
                  open: true,
                  message: "Enter a valid quantity (positive number)",
                  type: "error",
                });
              }
            }}
          >
            ✓
          </button>
          <button
            onClick={() => {
              setQuantityObj(initialQuantity);
              setShowEditView(false);
            }}
          >
            X
          </button>
          {"  " + displayNameForUnitOfMeasure(quantityObj.unit)}
          {isEditing && (
            <CircularProgress
              style={{ marginLeft: 5 }}
              size={15}
              color={"inherit"}
            />
          )}
        </>
      )}

      {!showEditView && (
        <span
          onClick={() => {
            setShowEditView(true);
          }}
        >
          {`${quantityObj.quantity} ${displayNameForUnitOfMeasure(
            quantityObj.unit
          )}`}{" "}
          <CreateIcon style={{ fontSize: 15 }} />
        </span>
      )}
    </>
  );
};

export const stockTransferForBranch = (
  allStockTransfers: IStockTransfersByBranch[],
  selectedBranch: IBranch
): IStockTransfer[] => {
  let stockTransfers: IStockTransfer[] = [];
  for (let i = 0; i < allStockTransfers.length; i++) {
    if (allStockTransfers[i].branchId === selectedBranch.id) {
      stockTransfers = allStockTransfers[i].stockTransfers;
      break;
    }
  }

  for (let i = 0; i < stockTransfers.length; i++) {
    const stockTransfer = stockTransfers[i];
    if (stockTransfer.senderBranchId === selectedBranch.id) {
      stockTransfer.inOut = "Outward";
      stockTransfer.toFrom = stockTransfer.receiverBranchName;
    } else {
      stockTransfer.inOut = "Inward";
      stockTransfer.toFrom = stockTransfer.senderBranchName;
    }
  }

  return stockTransfers;
};

export const StockTransferTable: React.FC<{ selectedBranch: IBranch }> = (
  props
) => {
  const {
    start,
    end,
    selectedDate,
    selectedDateFrom,
    selectedDateTo,
    handleDateChange,
    handleDateChangeFrom,
    handleDateChangeTo,
  } = useQuerySelectedFromToDates();
  const { status, stockTransfers, isFetching } = useQueryStockTransfers(
    start,
    end
  );
  const [stockTransfer, setStockTransfers] = useState<IStockTransfer[]>([]);
  useEffect(() => {
    if (status === "success") {
      if (!stockTransfers || stockTransfers instanceof FMSError) {
        const message = stockTransfers?.message ?? "Something went wrong!";
        setShowToast({
          open: true,
          message: message,
          type: "error",
        });
      } else {
        setStockTransfers(
          stockTransferForBranch(stockTransfers, props.selectedBranch)
        );
      }
    }
  }, [status, stockTransfers, props.selectedBranch]);

  const handleToastClose = () => {
    setShowToast({
      open: false,
      message: showToast.message,
      type: showToast.type,
    });
  };

  const [showToast, setShowToast] = useState<IToastBasicProps>({
    open: false,
    message: "",
    type: "success",
  });

  const [showApprove, setShowApprove] = useState(false);
  const [transferIdUnderApproval, setTransferIdUnderApproval] = useState<
    string | null
  >(null);
  const openApprove = (stockTransferId: string) => {
    setShowApprove(true);
    setTransferIdUnderApproval(stockTransferId);
  };

  const [isApproving, setApproving] = useState(false);
  const onApprove = async (stockTransferId: string) => {
    setApproving(true);
    const error = await approveStockTransfer(stockTransferId);
    setApproving(false);
    if (error) {
      const message = error.message ?? "Something went wrong!";
      setShowToast({
        open: true,
        message: message,
        type: "error",
      });
      return;
    }
    setShowApprove(false);
    setShowToast({
      open: true,
      message: "The item has been approved",
      type: "success",
    });

    if (showPending) {
      // this will refresh the pending stock list
      handleShowPendingButtonClicked(true);
    }

    invalidateStockTransferCache();
  };

  const { primaryColor } = useContext(FMSThemeContext);
  const [stockTransferUnderDelete, setStockTransferUnderDelete] =
    useState<IStockTransfer | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const onDeleteStockTransfer = async (stockTransferId: string) => {
    setIsDeleting(true);
    const error = await deleteStockTransfer(stockTransferId);
    setIsDeleting(false);
    setShowDeleteConfirmation(false);
    if (error == null) {
      setShowToast({
        open: true,
        message: "Deleted successfully",
        type: "success",
      });
      setStockTransfers((prev) =>
        prev.filter((s) => s.id !== stockTransferId)
      );
      if (showPending) {
        setPendingStockTransfers((prev) =>
          prev.filter((s) => s.id !== stockTransferId)
        );
      }
      invalidateStockTransferCache();
    } else {
      setShowToast({
        open: true,
        message: error.message ?? "Something went wrong!",
        type: "error",
      });
    }
  };

  const [isCommenting, setCommenting] = useState(false);
  const onComment = async (stockTransferId: string, comment: string) => {
    setCommenting(true);
    const error = await commentOnStockTransfer(stockTransferId, comment);
    setCommenting(false);
    if (error) {
      const message = error.message ?? "Something went wrong!";
      setShowToast({
        open: true,
        message: message,
        type: "error",
      });
      return;
    }
    setShowApprove(false);
    setShowToast({
      open: true,
      message: "Comment added on stock transfer",
      type: "success",
    });

    if (showPending) {
      // this will refresh the pending stock list
      handleShowPendingButtonClicked(true);
    }

    invalidateStockTransferCache();
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Branch Table",
        columns: [
          {
            Header: "Item",
            accessor: "materialName",
            sortType: (rowA: any, rowB: any) => {
              return rowA.original.materialName.toUpperCase() >
                rowB.original.materialName.toUpperCase()
                ? 1
                : -1;
            },
            width: 270,
            disableFilters: true,
          },
          {
            Header: "Quantity",
            sortType: (rowA: any, rowB: any) => {
              return rowA.original.quantity > rowB.original.quantity ? 1 : -1;
            },
            accessor: (row: IStockTransfer) => {
              const unit = displayNameForUnitOfMeasure(
                row.displayQuantity.unit
              );
              return `${row.displayQuantity.quantity} ${unit}`;
            },
            width: 180,
            disableGlobalFilter: true,
            disableFilters: true,
            Cell: (props: any) => {
              const stockTransfer = props.row.original as IStockTransfer;
              return (
                <EditableQuantity
                  stockTransfer={stockTransfer}
                  showToast={setShowToast}
                  quantityUpdated={(newQuantity) => {
                    stockTransfer.displayQuantity = newQuantity;
                    invalidateStockTransferCache();
                  }}
                />
              );
            },
          },
          {
            Header: "In / Out",
            accessor: "inOut",
            width: 120,
            disableGlobalFilter: true,
            disableFilters: true,
          },
          {
            Header: "To / From",
            accessor: "toFrom",
            width: 120,
            disableGlobalFilter: true,
            disableFilters: true,
          },
          {
            Header: "Date",
            sortType: (rowA: any, rowB: any) => {
              return rowA.original.requestInitiationDate >
                rowB.original.requestInitiationDate
                ? 1
                : -1;
            },
            accessor: (row: IStockTransfer) =>
              moment(row.requestInitiationDate).format("DD-MM-yyyy"),
            width: 120,
            disableGlobalFilter: true,
            disableFilters: true,
          },
          {
            Header: "Status",
            sortType: (rowA: any, rowB: any) => {
              const a = rowA.original;
              const b = rowB.original;
              if (a.status === b.status) {
                return a.materialName.toUpperCase() >
                  b.materialName.toUpperCase()
                  ? -1
                  : 1;
              }

              if (a.status === Status.PENDING) {
                return 1;
              }

              if (b.status === Status.PENDING) {
                return -1;
              }

              return 0;
            },
            accessor: "status",
            width: 140,
            disableGlobalFilter: true,
            disableFilters: true,
            Cell: (props: any) => {
              const stockTransfer = props.row.original as IStockTransfer;
              return (
                <StatusWithComments
                  stockTransder={stockTransfer}
                  openApprove={openApprove}
                  setShowToast={setShowToast}
                />
              );
            },
          },
          {
            Header: " ",
            accessor: "id",
            width: 50,
            disableFilters: true,
            disableGlobalFilter: true,
            Cell: (props: any) => {
              const stockTransfer = props.row.original as IStockTransfer;
              if (!isUserAuthorizedToModify()) {
                return <></>;
              }
              return (
                <Avatar
                  style={{
                    backgroundColor: primaryColor,
                    cursor: "pointer",
                    width: 32,
                    height: 32,
                  }}
                  onClick={() => {
                    setStockTransferUnderDelete(stockTransfer);
                    setShowDeleteConfirmation(true);
                  }}
                >
                  <DeleteIcon />
                </Avatar>
              );
            },
          },
        ],
      },
    ],
    [primaryColor]
  );

  const [showPending, setShowPending] = useState(false);
  const [pendingStockTransfers, setPendingStockTransfers] = useState<
    IStockTransfer[]
  >([]);
  const [pendingStatus, setPendingStatus] = useState<"loading" | "complete">();

  useEffect(() => {
    handleShowPendingButtonClicked(showPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedBranch]);

  const handleShowPendingButtonClicked = async (showPending: boolean) => {
    setShowPending(showPending);
    if (showPending) {
      setPendingStatus("loading");
      const result = await getPendingStockTransfers();
      setPendingStatus("complete");
      if (result instanceof FMSError) {
        const message = result.message ?? "Something went wrong!";
        setShowToast({
          open: true,
          message: message,
          type: "error",
        });
        return;
      }

      setPendingStockTransfers(
        stockTransferForBranch(result, props.selectedBranch)
      );
    }
  };

  return (
    <>
      <Toast
        message={showToast.message}
        open={showToast.open}
        onClose={handleToastClose}
        type={showToast.type}
      />
      <div
        style={{
          textAlign: "end",
        }}
      >
        <CSVDownload
          filename={`InFlow-OutFlow-${props.selectedBranch.name}-${moment(
            start
          ).format("DD MMM")}-to-${moment(end).format("DD MMM")}`}
          data={getStockTransferDataForExport(
            showPending ? pendingStockTransfers : stockTransfer
          )}
        />
      </div>
      <DataTableWithDates
        isLoading={isFetching || pendingStatus === "loading"}
        columns={columns}
        data={showPending ? pendingStockTransfers : stockTransfer}
        tableClass={styles.table}
        selectedDate={selectedDate!}
        selectedDateFrom={selectedDateFrom}
        selectedDateTo={selectedDateTo}
        handleDateChange={handleDateChange}
        handleDateChangeFrom={handleDateChangeFrom}
        handleDateChangeTo={handleDateChangeTo}
        showPendingButton={true}
        handleShowPendingButtonClicked={handleShowPendingButtonClicked}
        initialSortBy={"status"}
        initialSortOrder={"desc"}
      />
      {showApprove && transferIdUnderApproval !== null && (
        <ApprovalDialogWithComment
          open={showApprove}
          onClose={() => setShowApprove(false)}
          onApprove={() => onApprove(transferIdUnderApproval)}
          isApproving={isApproving}
          onComment={(comment) => onComment(transferIdUnderApproval, comment)}
          isCommenting={isCommenting}
        />
      )}
      <ConfirmationDialog
        buttonText={"Delete"}
        bodyText={
          stockTransferUnderDelete
            ? `Do you want to delete the ${
                stockTransferUnderDelete.inOut
              } transfer of ${stockTransferUnderDelete.materialName}: ${
                stockTransferUnderDelete.displayQuantity.quantity
              } ${displayNameForUnitOfMeasure(
                stockTransferUnderDelete.displayQuantity.unit
              )}?`
            : ""
        }
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onApprove={() => {
          if (stockTransferUnderDelete) {
            onDeleteStockTransfer(stockTransferUnderDelete.id);
          }
        }}
        isApproving={isDeleting}
      />
    </>
  );
};
